import { shapes } from '@joint/plus';
import React, { ChangeEvent, ReactElement, useCallback, useState } from 'react';

import Input from '../Input/Input';
import { useBaseInspector } from './useBaseInspector';

import { Switch } from '@mui/material';
import ReactQuill from 'react-quill';
import HelperCustomField from '../../HelperCustomField/HelperCustomField';

interface Props {
    cell: shapes.app.Service;
    chatbotFlowId: string;
}

/**
 * ServiceInspector (simplificado).
 *
 * Historicamente esse inspector tinha:
 *   - Select bugado de serviço (custom, sem Popper/flip/keyboard)
 *   - Botao "Testar servico" abrindo dialog inline
 *
 * Ambos foram migrados pra ServicePropertiesPanel — uma barra flutuante
 * profissional que aparece quando a caixa app.Service eh selecionada,
 * ancorada dentro do .paper-container. La o usuario:
 *   - Escolhe o servico (Autocomplete MUI com Portal + flip)
 *   - Cria servico novo inline (POST /integrationservice)
 *   - Edita headers/params/body/URL/method
 *   - Insere variaveis %chat.*% (ChatVariablesPicker)
 *   - Testa o servico (RequestIntegrationService reutilizado)
 *
 * Aqui ficam APENAS os campos do CELL (JointJS state, nao integration-service):
 *   - label (readonly)
 *   - isTextRich (switch)
 *   - description (texto ou rich)
 *   - displayText (rich sempre)
 *   - HelperCustomField (insercao de @param@/%var%/$custom$ no texto)
 */

const cellProps = {
    label: ['attrs', 'label', 'text'],
    description: ['attrs', 'description', 'text'],
    displayText: ['attrs', 'displayText', 'text'],
    icon: ['attrs', 'icon', 'xlinkHref'],
    portLabel: ['attrs', 'portLabel', 'text'],
    serviceId: ['attrs', 'serviceId', 'id'],
    isTextRich: ['attrs', 'isTextRich', 'isTextRich'],
};

const ServiceInspector = (props: Props): ReactElement => {

    const { cell, chatbotFlowId } = props;

    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [isTextRich, setIsTextRich] = useState<boolean>(false);
    const [displayText, setDisplayText] = useState<string>('');

    const [isDisplayTextOnFocus, setIsDisplayTextOnFocus] = useState<boolean>(false);

    const assignFormFields = useCallback((): void => {
        setLabel(cell.prop(cellProps.label));
        setDescription(cell.prop(cellProps.description));
        setIsTextRich(cell.prop(cellProps.isTextRich));
        setDisplayText(cell.prop(cellProps.displayText));
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    return (
        <>
            <h1>Componente selecionado</h1>

            {/* Aviso removido F6.A-13 — configuracao do integration-service vive
                agora na aba "Propriedades do Serviço" ao lado (default) OU no painel
                flutuante quando desacoplado. Nao precisa direcionar mais o user. */}

            <label>Label
                <Input type="text"
                       placeholder="Enter label"
                       value={label}
                       disabled={true}
                />
            </label>

            <div style={{ marginBottom: '8px', marginTop: '8px'}}>
                <Switch
                    value={isTextRich}
                    checked={isTextRich}
                    onChange={(e:any) => changeCellProp(cellProps.isTextRich, e?.target?.checked)}
                />
                <label>É um texto rico?</label>
            </div>

            {
                isTextRich
                ? (
                    <label>Descrição
                        <div style={{ resize: "vertical", overflow: "auto", height: "250px", marginBottom: "24px", border: "1px solid #ccc" }}>
                            <ReactQuill
                                name="description"
                                style={{ height: '100%' }}
                                fullWidth
                                variant="outlined"
                                id="descriprion"
                                value={description}
                                onFocus={(e) => setIsDisplayTextOnFocus(false)}
                                onChange={(value: any) => changeCellProp(cellProps.description, value)}
                            />
                        </div>
                    </label>
                )
                : (
                    <label>Descrição
                        <Input
                            type="text"
                            placeholder="Enter description"
                            value={description}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.description, e.target.value)}
                            onSetIsDisplayTextOnFocus={setIsDisplayTextOnFocus}
                        />
                    </label>
                )
            }

            <label>Texto de exibição
                <div style={{ resize: "vertical", overflow: "auto", height: "250px", marginBottom: "24px", border: "1px solid #ccc" }}>
                    <ReactQuill
                        name="description"
                        style={{ height: '100%' }}
                        fullWidth
                        variant="outlined"
                        id="descriprion"
                        onFocus={(e) => setIsDisplayTextOnFocus(true)}
                        value={displayText}
                        onChange={(value: any) => changeCellProp(cellProps.displayText, value)}
                    />
                </div>
            </label>

            <HelperCustomField
                isRichText={isDisplayTextOnFocus ? true : isTextRich}
                description={isDisplayTextOnFocus ? displayText : description}
                param1={isDisplayTextOnFocus ? cellProps.displayText : cellProps.description}
                onChangeDescription={changeCellProp}
                chatbotFlowId={chatbotFlowId}
            />
        </>
    );
};

export default ServiceInspector;
